/**
 * Duva API client — thin wrapper around fetch with JWT auth injection.
 */

// Must match the Worker that issues JWTs (see apps/worker/wrangler.toml APP_URL).
// Override with VITE_API_URL when using a custom API domain (e.g. api.getduva.com).
export const BASE_URL = import.meta.env.VITE_API_URL ?? 'https://api.getduva.com'

/** Marketing / SPA origin for invite links (matches worker DASHBOARD_URL host). */
export function dashboardOrigin(): string {
  const raw = (import.meta.env.VITE_DASHBOARD_URL as string | undefined) ?? 'https://www.getduva.com'
  try {
    const u = new URL(raw.includes('://') ? raw : `https://${raw}`)
    return `${u.protocol}//${u.host}`
  } catch {
    return 'https://www.getduva.com'
  }
}

/** Public Teams app (client) ID for the manifest helper on Settings. Optional VITE_TEAMS_APP_ID. */
export function teamsManifestAppId(): string {
  const v = (import.meta.env.VITE_TEAMS_APP_ID as string | undefined)?.trim()
  return v || 'REPLACE_WITH_YOUR_AZURE_APP_ID'
}

export function apiHostnameForManifest(): string {
  try {
    return new URL(BASE_URL).hostname
  } catch {
    return 'api.getduva.com'
  }
}

const DEFAULT_UPGRADE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string | undefined) ?? 'https://www.getduva.com/dashboard/settings'

/** Map worker freemium 403 bodies to an upgrade URL for UpgradeRequiredError. */
function freemiumUpgradeUrl(message: string): string | null {
  const billingHint =
    /Upgrade (to|for|at)\b|plan is limited|limited to \d+ seats|reports per month|plan or higher|requires .{0,48} plan or higher/i.test(
      message
    )
  const upgradeInUrl = message.match(/https?:\/\/[^\s]+upgrade[^\s]*/)
  if (upgradeInUrl) return upgradeInUrl[0]
  if (billingHint) {
    const anyHttps = message.match(/https?:\/\/[^\s]+/)
    if (anyHttps) return anyHttps[0]
    return DEFAULT_UPGRADE_URL
  }
  return null
}

// Thrown when API returns 403 with a freemium upgrade gate
export class UpgradeRequiredError extends Error {
  constructor(public override message: string, public upgradeUrl: string) {
    super(message)
    this.name = 'UpgradeRequiredError'
  }
}

const TOKEN_KEY = 'duva_token'
const REFRESH_KEY = 'duva_refresh_token'

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
}

export function setRefreshToken(refreshToken: string) {
  localStorage.setItem(REFRESH_KEY, refreshToken)
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

/** Trade OAuth redirect ?code= for JWT pair (single-use, server-side). */
export async function exchangeAuthCode(code: string): Promise<{ access_token: string; refresh_token: string }> {
  const res = await fetch(`${BASE_URL}/api/auth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  if (!res.ok) {
    throw new Error('Authorization code exchange failed')
  }
  return res.json() as Promise<{ access_token: string; refresh_token: string }>
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false
  try {
    const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return false
    const data = (await res.json()) as { access_token: string; refresh_token: string }
    setToken(data.access_token)
    setRefreshToken(data.refresh_token)
    return true
  } catch {
    return false
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const cache: RequestCache = token ? 'no-store' : 'default'
  let res = await fetch(`${BASE_URL}${path}`, { ...options, headers, cache })

  if (res.status === 401) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      token = getToken()
      if (token) headers['Authorization'] = `Bearer ${token}`
      res = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
        cache: getToken() ? 'no-store' : 'default',
      })
    }
    if (res.status === 401) {
      clearToken()
      window.location.href = '/login?reason=session_expired'
      throw new Error('Unauthorized')
    }
  }

  if (res.status === 403) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    const msg = (err as Record<string, string>).error ?? 'Access denied'
    const upgradeUrl = freemiumUpgradeUrl(msg)
    if (upgradeUrl) throw new UpgradeRequiredError(msg, upgradeUrl)
    throw new Error(msg)
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as Record<string, string>).error ?? 'API error')
  }

  return res.json()
}

/** Public waitlist signup (no JWT). */
export async function postWaitlist(email: string, challenge: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/waitlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase(), challenge }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as Record<string, string>).error ?? 'Could not join waitlist')
  }
}

export interface PricingUiConfig {
  visiblePlanNames?: string[]
  highlightPlanName?: string | null
  promoBadge?: string | null
}

/** Current user from GET /api/me (includes optional Cloudflare `cf.timezone` as `detected_timezone`). */
export interface MeProfile {
  id: string
  name: string
  email: string
  role: string
  preferred_channel: string
  nudge_frequency: string
  timezone: string | null
  created_at: string
  /** Slack workspace team id (T…), for deep links — same as org `slack_workspace_id`. */
  slack_team_id?: string | null
  detected_timezone?: string | null
}

/** Derived from avg. model P(negative); see feedback-themes API note. */
export type FeedbackThemeTone = 'positive' | 'mixed' | 'negative'

/** Admin-only: aggregated /ama anonymous feedback themes (no raw text). */
export interface FeedbackThemeRow {
  normalizedKey: string
  theme: string
  /** Longer context when available; may be null for very short legacy rows. */
  themeDetails: string | null
  count7d: number
  count30d: number
  countPrev7d: number
  avgSentiment7d: number | null
  avgSentiment30d: number | null
  tone7d: FeedbackThemeTone | null
  tone30d: FeedbackThemeTone | null
  trend: 'up' | 'down' | 'flat' | 'new'
}

export interface FeedbackThemesResponse {
  themes: FeedbackThemeRow[]
  totalSubmissions7d: number
  totalSubmissions30d: number
  note: string
}

export interface Geopricing {
  country: string
  countryName: string
  region: string
  plans: {
    name: string
    monthlyPrice: number
    annualPrice: number
    annualTotal: number
    currency: string
    symbol: string
    userLimit: number | null
    highlight?: boolean
    features: string[]
  }[]
  billingNote: string
  pppNote: string
  promoBadge?: string | null
  pricingUi?: PricingUiConfig | null
}

// ── API methods ────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  getSlackInstallUrl: () => `${BASE_URL}/api/auth/slack/install`,

  // Me — includes `detected_timezone` from Cloudflare edge when available (see worker). You choose when to apply it in UI.
  getMe: () =>
    request<MeProfile>('/api/me'),
  patchMe: (body: { timezone: string | null }) =>
    request<{ status: string; timezone: string | null }>('/api/me', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getGoal: () => request<{ goal: string; track: string; updatedAt: string } | null>('/api/me/goal'),
  postGoal: (goal: string) => request<{ goal: string; track: string; updatedAt: string }>('/api/me/goal', {
    method: 'POST',
    body: JSON.stringify({ goal })
  }),
  getNudgeProgress: () =>
    request<{
      streakDays: number
      completedThisWeek: number
      completedThisMonth: number
      snoozedThisWeek: number
      sentThisWeek: number
    }>('/api/me/nudge-progress'),

  // Analytics
  getEngagement: (days = 30) => request<{ scores: unknown[] }>(`/api/analytics/engagement?days=${days}`),
  getNudgeStats: (days = 30) => request<{ stats: unknown[] }>(`/api/analytics/nudges?days=${days}`),
  getSummary: () => request<{
    engagementScore: { score: number; computed_at: string } | null;
    riskFlags: string[];
    frictionScore: number;
    weeklyStats: { sent: number; opened: number; acted: number } | null;
    activeMemberCount: number;
    isAnonymized: boolean;
  }>('/api/analytics/summary'),
  getMabStats: () => request<{ stats: { category: string; success_count: number; total_count: number; conversion_rate: number }[] }>('/api/analytics/mab'),
  getInsights: () => request<{ insight: string; generatedAt: string }>('/api/analytics/insights'),
  getPulseStats: (daysOrMonth: number | 'month' = 30) => {
    const qs = daysOrMonth === 'month' ? 'period=month' : `days=${daysOrMonth}`
    return request<{
      stats: {
        inspiring: number
        developing: number
        planning: number
        vision: number
        change: number
        response_count: number
      } | null
      isAnonymized: boolean
      count?: number
      monthLabel?: string
    }>(`/api/analytics/pulse?${qs}`)
  },

  // Nudges
  getNudges: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return request<{ nudges: unknown[]; total: number }>(`/api/nudges${qs}`)
  },
  sendNudge: (body: { userIds: string[]; nudgeId?: string; idempotencyKey: string }) =>
    request('/api/nudges/send', { method: 'POST', body: JSON.stringify(body) }),
  updateDelivery: (deliveryId: string, body: { outcome: string }) =>
    request<{ status: string; isFirstNudge?: boolean }>(`/api/nudges/${deliveryId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Org
  getOrgSettings: () =>
    request<{
      id: string
      name: string
      plan: string
      employee_count: number
      slack_workspace_id: string | null
    }>('/api/org/settings'),
  updateOrgSettings: (body: { name?: string; plan?: string; industry?: string | null }) =>
    request('/api/org/settings', { method: 'PATCH', body: JSON.stringify(body) }),
  upgradePlan: (plan: string) =>
    request('/api/org/settings', { method: 'PATCH', body: JSON.stringify({ plan }) }),
  getMembers: () => request<{ members: unknown[] }>('/api/org/members'),
  updateMember: (
    userId: string,
    body: {
      nudgeTrack?: string | null
      nudgeFrequency?: string
      preferredChannel?: string
      role?: string
      timezone?: string | null
    }
  ) => request(`/api/org/members/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  syncMembers: () => request('/api/org/members/sync', { method: 'POST' }),
  getOrgFeatures: () =>
    request<{ plan: string; available: string[]; disabled: string[]; all: string[] }>(
      '/api/org/features'
    ),
  updateOrgFeatures: (disabled: string[]) =>
    request<{ status: string; disabled: string[] }>('/api/org/features', {
      method: 'PATCH',
      body: JSON.stringify({ disabled }),
    }),
  getOrgDeliveryHealth: () =>
    request<{
      deliveriesLast24h: number
      deliveriesLast7d: number
      deliveriesLast30d: number
      lastDeliverySentAt: string | null
      usersWithNudgesOn: number
      usersEligibleNoDeliveryTodayUtc: number
      approvedNudgeTemplates: number
      schedulerNote: string
    }>('/api/org/delivery-health'),

  getFeedbackThemes: () =>
    request<FeedbackThemesResponse>('/api/org/feedback-themes'),

  // Reports
  generateReport: (opts?: { timezone?: string | null }) =>
    request<{ reportId: string; shareUrl: string; generatedAt?: string }>('/api/reports/share', {
      method: 'POST',
      body: JSON.stringify({
        timezone: opts?.timezone?.trim() || undefined,
      }),
    }),

  // Accountability
  getAccountabilityPairs: () =>
    request<{ pairs: AccountabilityPair[] }>('/api/accountability/pairs'),
  runAccountabilityMatching: () =>
    request<{ pairsCreated: number; pairs: AccountabilityPair[] }>('/api/accountability/pairs/run', { method: 'POST' }),
  deactivatePair: (pairId: string) =>
    request(`/api/accountability/pairs/${pairId}`, { method: 'DELETE' }),

  // Invites
  getInvites: () => request<{ invites: Invite[] }>('/api/invites'),
  sendInvites: (emails: string[], role?: string) =>
    request<{ results: { email: string; status: string }[] }>('/api/invites', {
      method: 'POST',
      body: JSON.stringify({ emails, role }),
    }),
  revokeInvite: (id: string) => request(`/api/invites/${id}`, { method: 'DELETE' }),

  // SSO
  getSsoStatus: () =>
    request<{ configured: boolean; provider: string | null; domain: string | null }>('/api/sso/status'),
  configureSso: (body: { provider: string; issuer_url: string; client_id: string; client_secret: string; domain: string }) =>
    request('/api/sso/configure', { method: 'POST', body: JSON.stringify(body) }),
  getSsoLoginUrl: (domain: string) =>
    `${BASE_URL}/api/sso/login?domain=${encodeURIComponent(domain)}`,

  // Nudge History (personal)
  getNudgeHistory: () =>
    request<{ deliveries: NudgeDelivery[] }>('/api/me/nudge-history'),

  // Streak Shield
  getStreakShield: () =>
    request<{ available: boolean; usedThisMonth: boolean }>('/api/me/streak-shield'),
  useStreakShield: () =>
    request<{ used: boolean; reason?: string }>('/api/me/streak-shield/use', { method: 'POST' }),

  // Track examples (goal → track explainer)
  getTrackExamples: (track: string) =>
    request<{ examples: string[] }>(`/api/org/track-examples?track=${encodeURIComponent(track)}`),

  // Benchmarking & ROI
  getBenchmark: () => request<{ orgRate: number; industryRate: number; industryName: string; isAvailable: boolean; peerCount: number }>('/api/analytics/benchmark'),
  getRoi: (days = 30) => request<{ 
    totalActions: number; 
    totalOpened: number; 
    estimatedValue: number; 
    valuePerAction: number; 
    frictionScore: number; 
    labels: { headline: string; subline: string } 
  }>(`/api/analytics/roi?days=${days}`),

  // Custom Nudges
  submitCustomNudge: (body: { title: string; body: string; category: string }) =>
    request('/api/org/nudges/submit', { method: 'POST', body: JSON.stringify(body) }),
  getCustomNudges: () => request<{ nudges: any[] }>('/api/org/nudges/custom'),

  // Delivery alert threshold (admin)
  getDeliveryThreshold: () =>
    request<{ threshold: number | null }>('/api/org/delivery-threshold'),
  setDeliveryThreshold: (threshold: number | null) =>
    request<{ threshold: number | null }>('/api/org/delivery-threshold', {
      method: 'PATCH',
      body: JSON.stringify({ threshold }),
    }),

  // CSV export (returns raw blob — use downloadNudgeStatsCsv helper)
  getNudgeStatsCsvUrl: (days = 30) =>
    `${BASE_URL}/api/analytics/nudges/csv?days=${days}`,

  // Pricing
  getGeoPricing: () => request<Geopricing>('/api/pricing/geo'),

  postWaitlist,
}

/** Downloads the nudge stats CSV. Requires an active JWT in localStorage. */
export async function downloadNudgeStatsCsv(days = 30): Promise<void> {
  const token = localStorage.getItem('duva_token')
  const res = await fetch(`${BASE_URL}/api/analytics/nudges/csv?days=${days}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: 'no-store',
  })
  if (!res.ok) throw new Error('CSV export failed')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `nudge-stats-${days}d.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}


const PLATFORM_TOKEN_KEY = 'duva_platform_admin_token'

export function getPlatformAdminToken(): string | null {
  return sessionStorage.getItem(PLATFORM_TOKEN_KEY)
}

export function setPlatformAdminToken(token: string) {
  sessionStorage.setItem(PLATFORM_TOKEN_KEY, token)
}

export function clearPlatformAdminToken() {
  sessionStorage.removeItem(PLATFORM_TOKEN_KEY)
}

async function platformRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getPlatformAdminToken()
  if (!token) throw new Error('Missing platform admin token')
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string> ?? {}),
    },
    cache: 'no-store',
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    const o = err as Record<string, string>
    throw new Error(o.error ?? o.message ?? `API error ${res.status}`)
  }
  return res.json()
}

export const platformAdminApi = {
  listWorkspaces: (q?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams()
    if (q?.limit != null) params.set('limit', String(q.limit))
    if (q?.offset != null) params.set('offset', String(q.offset))
    const qs = params.toString() ? `?${params}` : ''
    return platformRequest<{ workspaces: Record<string, unknown>[]; total: number; limit: number; offset: number }>(
      `/api/platform/workspaces${qs}`
    )
  },
  getWorkspace: (orgId: string) =>
    platformRequest<{ workspace: Record<string, unknown>; members: Record<string, unknown>[] }>(
      `/api/platform/workspaces/${orgId}`
    ),
  patchWorkspace: (orgId: string, body: { plan?: string; industry?: string | null }) =>
    platformRequest<{ workspace: Record<string, unknown> }>(`/api/platform/workspaces/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getPricingUi: () => platformRequest<{ pricingUi: PricingUiConfig | null }>('/api/platform/settings/pricing-ui'),
  patchPricingUi: (body: PricingUiConfig) =>
    platformRequest<{ pricingUi: PricingUiConfig }>('/api/platform/settings/pricing-ui', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getAudit: (limit = 50) =>
    platformRequest<{ entries: Record<string, unknown>[] }>(`/api/platform/audit?limit=${limit}`),
  getNudgeStats: () =>
    platformRequest<{
      byStatus: { status: string; count: number }[]
      approvedByCategory: { category: string; count: number }[]
      totalApproved: number
      totalAll: number
    }>('/api/platform/nudges/stats'),
  listPlatformNudges: (q?: { status?: string; scope?: string }) => {
    const params = new URLSearchParams()
    if (q?.status) params.set('status', q.status)
    if (q?.scope) params.set('scope', q.scope)
    const qs = params.toString() ? `?${params}` : ''
    return platformRequest<{ nudges: Record<string, unknown>[] }>(`/api/platform/nudges${qs}`)
  },
  patchPlatformNudge: (id: string, body: { status?: string; review_notes?: string | null }) =>
    platformRequest<{ nudge: Record<string, unknown> }>(`/api/platform/nudges/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  listWaitlist: (limit = 500) =>
    platformRequest<{
      signups: { email: string; challenge_id: string; created_at: string }[]
      total: number
      limit: number
    }>(`/api/platform/waitlist?limit=${limit}`),
  processBriefing: (briefing: string) =>
    platformRequest<{
      nudges: StudioProcessedNudge[]
      llm_provider: 'claude' | 'workers_ai'
      briefing_truncated: boolean
    }>('/api/platform/process-briefing', {
      method: 'POST',
      body: JSON.stringify({ briefing }),
    }),
  saveStudioNudge: (body: StudioSaveRequest) =>
    platformRequest<{ nudge: Record<string, unknown> }>('/api/platform/nudges/studio-save', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  bulkApproveNudges: (ids: string[]) =>
    platformRequest<{ approved: number; total: number }>('/api/platform/nudges/bulk-approve', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  getAuditFiltered: (limit = 50, action?: string) => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (action) params.set('action', action)
    return platformRequest<{ entries: Record<string, unknown>[] }>(`/api/platform/audit?${params}`)
  },
}

/** Nudge Studio — matches worker `StudioProcessedNudge` / API response */
export type StudioProcessedNudge = {
  title: string
  category: string
  science_source: string
  insight: string
  nudge_text: string
  timing_cue: string
  system_target: 'system1' | 'system2'
  effectiveness_score: number
  is_potential_duplicate: boolean
  duplicate_of_id: string | null
  duplicate_reason?: string | null
  existing_nudge?: {
    id: string
    title: string
    body: string
    science_source: string | null
    category: string
  } | null
  llm_provider?: 'claude' | 'workers_ai'
}

export type StudioSaveRequest = {
  title: string
  category: string
  science_source: string
  insight: string
  nudge_text: string
  timing_cue: string
  system_target?: 'system1' | 'system2'
  effectiveness_score?: number
  channel?: 'slack' | 'email' | 'any'
  studio_raw: Record<string, unknown>
}

// ── Shared types (web-side) ────────────────────────────────────────────────────
export interface Invite {
  id: string
  email: string
  role: string
  token?: string
  created_at: string
  expires_at: string
  invited_by_name: string
}

export interface NudgeDelivery {
  id: string
  sent_at: string
  outcome: string | null
  title: string
  category: string
}

export interface AccountabilityPair {
  id: string
  nudge_track: string
  paired_at: string
  user_a_id: string
  user_a_name: string
  user_a_email: string
  user_a_slack_id?: string | null
  user_a_teams_id?: string | null
  user_b_id: string
  user_b_name: string
  user_b_email: string
  user_b_slack_id?: string | null
  user_b_teams_id?: string | null
}
